import { apiSlice } from './apiSlice';
import { ICulture, ICultureTranslations, ITranslationItem } from '@Types/Translation';

interface IAddCultureParams {
  cultureCode: string;
  displayName: string;
}

interface ISaveCultureTranslationsParams {
  cultureId: string;
  translations: ITranslationItem[];
}

const translationsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    cultures: builder.query<ICulture[], void>({
      query: () => ({
        url: '/api/Translations/GetCultures',
        method: 'GET',
      }),
      transformResponse: (response: { items: ICulture[] }) => response.items,
      keepUnusedDataFor: 0,
      providesTags: ['cultures'] as any,
    }),

    addCulture: builder.mutation<ICulture, IAddCultureParams>({
      query: (data) => ({
        url: '/api/Translations/AddCulture',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['cultures'] as any,
    }),

    deleteCulture: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/Translations/DeleteCulture?id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['cultures'] as any,
    }),

    cultureTranslations: builder.query<ICultureTranslations, string>({
      query: (cultureId) => ({
        url: `/api/Translations/GetCultureTranslations?cultureId=${cultureId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: ['culture-translations'] as any,
    }),

    saveCultureTranslations: builder.mutation<
      ICultureTranslations,
      ISaveCultureTranslationsParams
    >({
      query: (data) => ({
        url: '/api/Translations/SaveCultureTranslations',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['cultures', 'culture-translations'] as any,
    }),

    // Public/app-facing endpoint: flat key/value dictionary for one culture,
    // used to render the portal's own UI in the selected language.
    getTranslationsByCulture: builder.query<Record<string, string>, string>({
      query: (cultureCode) => ({
        url: `/api/Translations/GetByCulture?cultureCode=${cultureCode}`,
        method: 'GET',
      }),
      transformResponse: (response: { translations: Record<string, string> }) =>
        response.translations,
    }),
  }),
  overrideExisting: false,
});

export const {
  useCulturesQuery,
  useAddCultureMutation,
  useDeleteCultureMutation,
  useCultureTranslationsQuery,
  useSaveCultureTranslationsMutation,
  useGetTranslationsByCultureQuery,
} = translationsSlice;
